import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useApplyExternalPayment } from 'src/hooks/api/useApplyExternalPayment';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import { useUpsertBuyer } from 'src/hooks/api/useUpsertBuyer';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  BuyerUpsertDto,
  BuyerWithSalesDto,
  CreateRetailSaleWithPaymentResponse,
  EventWithData,
  GenericAddressDto,
  ListingDetails,
  SaleClient,
  SaleLineItemCoverage,
  Tag,
} from 'src/WebApiController';

import {
  buildRetailSaleGroupInput,
  computeProportionalCoverages,
  getPaymentAmount,
  getPaymentCountryCode,
  getRetailMerchantReferenceId,
} from './RetailSaleLocalContext.utils';
import { RetailSaleFormValues } from './types';

/**
 * POS-8322: Apply form tags to all created sales. Uses SaleClient.updateSaleBySaleId
 * with the tags wrapped in a PosField (hasChanged: true). Best-effort.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyTagsToSales = async (
  tags: Tag[] | null | undefined,
  saleIds: number[],
  config: any
) => {
  if (!tags || tags.length === 0 || saleIds.length === 0) return;
  const tagsWithValues = tags.filter((t) => t.value);
  if (tagsWithValues.length === 0) return;
  const saleClient = new SaleClient(config);
  for (const saleId of saleIds) {
    try {
      await saleClient.updateSaleBySaleId(saleId, {
        saleId,
        tags: { value: tagsWithValues, hasChanged: true },
      } as any);
    } catch (e) {
      console.error('applyTagsToSales failed for sale', saleId, e);
    }
  }
};

/**
 * Post-sale navigation resolver. Every retail sale is now created as a
 * SalesGroup (single or multi); the response's `sales.length` tells us
 * which case we're in and thus where to route:
 *
 *  - length === 1: single-listing sale → navigate to the sale detail
 *    modal (legacy behavior).
 *  - length > 1: multi-listing sale (POS-8649) → navigate to /sales with
 *    the salesGroupId query param so the SalesGroupModal (POS-8632)
 *    picks the group up.
 *
 * Only the multi-listing branch depends on the SalesGroupModal being
 * merged. Until POS-8632 lands, the multi-listing case still routes
 * correctly by URL — the modal just isn't there to open yet.
 */
const resolvePostSaleNavigation = (
  _sales: CreateRetailSaleWithPaymentResponse[] | undefined,
  salesGroupId: string
): string => {
  // POS-8918: Always open the Sales Group modal after creation — even for
  // single-listing sales — so the user can immediately see the group view
  // with payment status, invoice, and member-sale details.
  return `/sales?uniqueSalesGroupId=${salesGroupId}`;
};

export type AdyenSession = {
  saleId: number;
  salesGroupId: string | null;
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  merchantReference: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type UseRetailSaleOperationsParams = {
  event: EventWithData | undefined;
  listing: ListingDetails | undefined;
  selectedBuyer: BuyerWithSalesDto | undefined;
  selectedStoredPaymentMethodId: string | null;
  setSelectedBuyer: (buyer: BuyerWithSalesDto) => void;
  isPartialPayment: boolean;
  partialAmount: number;
};

export const useRetailSaleOperations = ({
  listing,
  selectedBuyer,
  selectedStoredPaymentMethodId,
  setSelectedBuyer,
  isPartialPayment,
  partialAmount,
}: UseRetailSaleOperationsParams) => {
  const { getValues } = useFormContext<RetailSaleFormValues>();
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const navigate = useNavigate();

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const applyExternalPayment = useApplyExternalPayment();
  const upsertBuyer = useUpsertBuyer();
  const knownCountries = useKnownCountries();

  // Reference to track the created sale
  // In case of payment error, we want to avoid creating multiple sales for the same listing
  const saleIdRef = useRef<number | null>(null);
  // Every retail sale belongs to a sales group (a single sale is a group of one); Adyen
  // sessions/payments/refunds key off this group id.
  const salesGroupIdRef = useRef<string | null>(null);
  // Full sales array from the createRetailSaleGroup response. Retained across the
  // payment step so post-payment Adyen success paths can pick the right destination
  // (single-sale detail vs multi-listing /sales?salesGroupId=…) via
  // resolvePostSaleNavigation, without re-plumbing sales.length through each callback.
  const salesRef = useRef<CreateRetailSaleWithPaymentResponse[] | undefined>(
    undefined
  );

  const [adyenSession, setAdyenSession] = useState<AdyenSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const disableEditing = useMemo(() => {
    return isLoading || Boolean(adyenSession);
  }, [adyenSession, isLoading]);

  const onCompleteAsUnpaid = useCallback(() => {
    const values = getValues();
    setIsLoading(true);
    const groupInput = buildRetailSaleGroupInput(
      values,
      listing,
      selectedBuyer
    );

    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);
        const result = await client.createRetailSaleGroup(groupInput);
        const saleId = result.sales?.[0]?.saleId;

        if (result.success && saleId) {
          const saleIds = result.sales?.map((s) => s.saleId) ?? [];
          await applyTagsToSales(
            values.tags,
            saleIds,
            activeAccountWebClientConfig
          );
          navigate(
            resolvePostSaleNavigation(result.sales, result.salesGroupId)
          );
        } else {
          showErrorDialog(
            'SaleClient.createRetailSaleGroup',
            {
              message: result.message ?? genericError,
              // POS-8322: Use 400 (validation error) not 500 (server error) so the
              // dialog shows the message instead of "Unexpected Error".
              status: 400,
            } as ApiException,
            { trackErrorData: groupInput }
          );
        }
      },
      (error) => {
        showErrorDialog('SaleClient.createRetailSaleGroup', error, {
          trackErrorData: groupInput,
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    genericError,
    getValues,
    listing,
    navigate,
    selectedBuyer,
    showErrorDialog,
  ]);

  /**
   * Submits the retail sale when the broker selects "Paid Externally".
   *
   * Full payment (isPartialPayment === false):
   *   Sends external payment fields directly with createRetailSaleGroup
   *   so the sale is created as Paid in a single call (Step 1 path).
   *
   * Partial payment (isPartialPayment === true):
   *   1. Creates the sale as Unpaid (no external fields on create)
   *   2. Auto-computes proportional coverages across unpaid lines
   *   3. Calls ApplyExternalPayment with the computed coverages.
   */
  const onCompleteExternalPayment = useCallback(() => {
    const values = getValues();
    setIsLoading(true);

    if (isPartialPayment) {
      // Partial path: create sale as Unpaid (if not already created),
      // then auto-compute coverages and apply external payment.
      const groupInput = buildRetailSaleGroupInput(
        values,
        listing,
        selectedBuyer,
        false
      );

      tryInvokeApi(
        async () => {
          if (!saleIdRef.current) {
            const client = new SaleClient(activeAccountWebClientConfig);
            const result = await client.createRetailSaleGroup(groupInput);
            const saleId = result.sales?.[0]?.saleId;

            if (!result.success || !saleId) {
              showErrorDialog(
                'SaleClient.createRetailSaleGroup',
                {
                  message: result.message ?? genericError,
                  status: 400,
                } as ApiException,
                { trackErrorData: groupInput }
              );
              return;
            }

            saleIdRef.current = saleId;
            salesGroupIdRef.current = result.salesGroupId;
            salesRef.current = result.sales;
            const saleIds = result.sales?.map((s) => s.saleId) ?? [];
            await applyTagsToSales(
              values.tags,
              saleIds,
              activeAccountWebClientConfig
            );
          }

          // Fetch unpaid lines and auto-compute proportional coverages
          const client = new SaleClient(activeAccountWebClientConfig);
          const salesData = await client.getSalesInSalesGroup(
            salesGroupIdRef.current!
          );
          const coverages = computeProportionalCoverages(
            partialAmount,
            salesData
          );

          await applyExternalPayment.mutateAsync({
            saleId: saleIdRef.current!,
            input: {
              externalPaymentMethodType: values.externalPaymentMethodType!,
              externalPaymentNote: values.externalPaymentNote ?? null,
              externalPaymentAmount: partialAmount,
              externalPaymentDate: values.externalPaymentDate ?? null,
              coverages,
            },
          });
          navigate(
            resolvePostSaleNavigation(
              salesRef.current,
              salesGroupIdRef.current!
            )
          );
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSaleGroup', error, {
            trackErrorData: groupInput,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    } else {
      // Step 1 full-payment path: include external fields so the sale is created Paid.
      const groupInput = buildRetailSaleGroupInput(
        values,
        listing,
        selectedBuyer,
        true
      );

      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          const result = await client.createRetailSaleGroup(groupInput);
          const saleId = result.sales?.[0]?.saleId;

          if (result.success && saleId) {
            const saleIds = result.sales?.map((s) => s.saleId) ?? [];
            await applyTagsToSales(
              values.tags,
              saleIds,
              activeAccountWebClientConfig
            );
            navigate(
              resolvePostSaleNavigation(result.sales, result.salesGroupId)
            );
          } else {
            showErrorDialog(
              'SaleClient.createRetailSaleGroup',
              {
                message: result.message ?? genericError,
                status: 400,
              } as ApiException,
              { trackErrorData: groupInput }
            );
          }
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSaleGroup', error, {
            trackErrorData: groupInput,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    }
  }, [
    activeAccountWebClientConfig,
    applyExternalPayment,
    genericError,
    getValues,
    isPartialPayment,
    listing,
    navigate,
    partialAmount,
    selectedBuyer,
    showErrorDialog,
  ]);

  const onPayAndComplete = useCallback(() => {
    const storedMethodId = selectedStoredPaymentMethodId;
    const values = getValues();
    setIsLoading(true);

    tryInvokeApi(
      async () => {
        if (!saleIdRef.current) {
          // Create the sale as Unpaid (omit external fields); the Adyen session/
          // payment below settles it.
          const groupInput = buildRetailSaleGroupInput(
            values,
            listing,
            selectedBuyer,
            false
          );
          const client = new SaleClient(activeAccountWebClientConfig);
          const placeholderResult =
            await client.createRetailSaleGroup(groupInput);
          const placeholderSaleId = placeholderResult.sales?.[0]?.saleId;

          if (!placeholderResult.success || !placeholderSaleId) {
            showErrorDialog(
              'SaleClient.createRetailSaleGroup',
              {
                message: placeholderResult.message ?? genericError,
                status: 400,
              } as ApiException,
              { trackErrorData: groupInput }
            );
            return;
          }

          saleIdRef.current = placeholderSaleId;
          salesGroupIdRef.current = placeholderResult.salesGroupId;
          salesRef.current = placeholderResult.sales;
          const saleIds = placeholderResult.sales?.map((s) => s.saleId) ?? [];
          await applyTagsToSales(
            values.tags,
            saleIds,
            activeAccountWebClientConfig
          );
        }

        // If partial payment, auto-compute proportional coverages
        let coverages: SaleLineItemCoverage[] | null = null;
        let amount = getPaymentAmount(values);
        if (isPartialPayment) {
          const client = new SaleClient(activeAccountWebClientConfig);
          const salesData = await client.getSalesInSalesGroup(
            salesGroupIdRef.current!
          );
          coverages = computeProportionalCoverages(partialAmount, salesData);
          amount = partialAmount;
        }

        const merchantReference = getRetailMerchantReferenceId(
          saleIdRef.current
        );

        try {
          if (storedMethodId) {
            const paymentStatus =
              await submitPaymentWithStoredMethod.mutateAsync({
                salesGroupId: salesGroupIdRef.current,
                storedPaymentMethodId: storedMethodId,
                amount,
                currencyCode: values.currencyCode ?? 'USD',
                buyerEmail: values.buyerEmail ?? null,
                reference: merchantReference,
                lineItemCoverages: coverages,
              });
            if (!paymentStatus.settled) {
              showErrorDialog(
                'RetailSale.onPayAndComplete',
                {
                  message:
                    'Payment was received but is still being finalized. Please check the sale status in a moment.',
                },
                { trackErrorData: { saleId: saleIdRef.current } }
              );
              return;
            }
            navigate(
              resolvePostSaleNavigation(
                salesRef.current,
                salesGroupIdRef.current!
              )
            );
          } else {
            const countryCode = getPaymentCountryCode(
              selectedBuyer,
              knownCountries.data
            );
            const sessionResponse = await createPaymentSession.mutateAsync({
              salesGroupId: salesGroupIdRef.current,
              amount,
              currencyCode: values.currencyCode ?? 'USD',
              buyerEmail: values.buyerEmail ?? null,
              returnUrl: window.location.href,
              reference: merchantReference,
              countryCode,
              lineItemCoverages: coverages,
            });

            setAdyenSession({
              saleId: saleIdRef.current,
              salesGroupId: salesGroupIdRef.current,
              sessionId: sessionResponse.sessionId,
              sessionData: sessionResponse.sessionData,
              clientKey: sessionResponse.clientKey,
              environment: sessionResponse.environment,
              merchantReference,
              countryCode,
              lineItemCoverages: coverages,
            });
          }
        } catch (error: any) {
          trackError('AdyenPayment.onError', new Error(error.message));
          navigate(
            resolvePostSaleNavigation(
              salesRef.current,
              salesGroupIdRef.current!
            )
          );
        }
      },
      (error) => {
        showErrorDialog('RetailSale.onPayAndComplete', error, {
          trackErrorData: { listingId: listing?.id ?? values.listingId },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    createPaymentSession,
    genericError,
    getValues,
    isPartialPayment,
    knownCountries.data,
    listing,
    navigate,
    partialAmount,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    showErrorDialog,
    submitPaymentWithStoredMethod,
    trackError,
  ]);

  const onAdyenPaymentCompleted = useCallback(
    async (result: AdyenPaymentCompletedResult) => {
      if (!adyenSession) return;

      if (result.resultCode !== 'Authorised') {
        setIsLoading(false);
        setAdyenSession(null);
        showErrorDialog(
          'RetailSale.onPayAndComplete',
          {
            message: `Payment not authorized. Result code: ${result.resultCode}`,
          },
          {
            trackErrorData: {
              resultCode: result.resultCode,
              saleId: adyenSession.saleId,
            },
          }
        );
        return;
      }

      await tryInvokeApi(
        async () => {
          const paymentStatus = await submitPaymentResult.mutateAsync({
            salesGroupId: adyenSession.salesGroupId,
            input: {
              resultCode: result.resultCode,
              sessionResult: result.sessionResult ?? null,
              merchantReference: adyenSession.merchantReference,
              pspReference: null,
              lineItemCoverages: adyenSession.lineItemCoverages,
            },
          });

          if (!paymentStatus.settled) {
            // Charge authorised at Adyen but POS settlement did not commit.
            // The AUTHORISATION webhook will retry settlement — show a transitional state.
            showErrorDialog(
              'RetailSale.onPayAndComplete',
              {
                message:
                  'Payment was received but is still being finalized. Please check the sale status in a moment.',
              },
              {
                trackErrorData: {
                  resultCode: paymentStatus.resultCode,
                  saleId: adyenSession.saleId,
                },
              }
            );
            return;
          }

          navigate(
            resolvePostSaleNavigation(
              salesRef.current,
              adyenSession.salesGroupId ?? ''
            )
          );
        },
        (error) => {
          showErrorDialog('RetailSale.onPayAndComplete', error, {
            trackErrorData: {
              listingId: listing?.id,
              saleId: adyenSession.saleId,
            },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      adyenSession,
      listing?.id,
      navigate,
      setIsLoading,
      showErrorDialog,
      submitPaymentResult,
    ]
  );

  const onAdyenError = useCallback(
    (error: { message: string }) => {
      trackError('AdyenDropIn.onError', new Error(error.message));
      setAdyenSession(null);
    },
    [setAdyenSession, trackError]
  );

  const onUpsertBuyer = useCallback(async (): Promise<boolean> => {
    const values = getValues();

    const needsUpsert =
      !selectedBuyer ||
      !selectedBuyer.shippingAddressLine1 ||
      !selectedBuyer.shippingCity ||
      !selectedBuyer.shippingState ||
      !selectedBuyer.shippingZipCode ||
      !selectedBuyer.shippingCountry ||
      !selectedBuyer.billingAddressLine1 ||
      !selectedBuyer.billingCity ||
      !selectedBuyer.billingState ||
      !selectedBuyer.billingZipCode ||
      !selectedBuyer.billingCountry;

    if (!needsUpsert) return true;

    const buildAddress = (
      street: string | undefined,
      suite: string | undefined,
      city: string | undefined,
      state: string | undefined,
      zipCode: string | undefined,
      country: string | undefined
    ): GenericAddressDto | null => {
      if (!street && !state && !zipCode && !country) return null;
      return {
        genericAddressId: 0,
        addressLine1: street ?? null,
        addressLine2: suite ?? null,
        city: city ?? null,
        state: state ?? null,
        zipCode: zipCode ?? null,
        country: country ?? null,
      };
    };

    const shippingAddress = buildAddress(
      values.shippingStreet,
      values.shippingSuite,
      values.shippingCity,
      values.shippingState,
      values.shippingZipCode,
      values.shippingCountry
    );
    const billingAddress = values.billingAddressSameAsShipping
      ? shippingAddress
      : buildAddress(
          values.billingStreet,
          values.billingSuite,
          values.billingCity,
          values.billingState,
          values.billingZipCode,
          values.billingCountry
        );

    const dto: BuyerUpsertDto = {
      buyerEmail: selectedBuyer?.buyerEmail ?? values.buyerEmail!,
      buyerName: selectedBuyer?.buyerName ?? values.buyerName ?? null,
      buyerPhoneNumber:
        selectedBuyer?.buyerPhoneNumber ?? values.buyerPhone ?? null,
      internalNotes:
        selectedBuyer?.internalNotes ?? values.customerMessage ?? null,
      lastRetailActivityDate: null,
      shippingAddress,
      billingAddress,
      externalId: null,
    };

    setIsLoading(true);
    try {
      await upsertBuyer.mutateAsync(dto);

      setSelectedBuyer({
        ...(selectedBuyer ?? {}),
        buyerEmail: dto.buyerEmail,
        buyerName: dto.buyerName,
        buyerPhoneNumber: dto.buyerPhoneNumber,
        internalNotes: selectedBuyer?.internalNotes ?? null,
        lastRetailActivityDate: selectedBuyer?.lastRetailActivityDate ?? null,
        shippingAddressLine1: shippingAddress?.addressLine1 ?? null,
        shippingAddressLine2: shippingAddress?.addressLine2 ?? null,
        shippingCity: shippingAddress?.city ?? null,
        shippingState: shippingAddress?.state ?? null,
        shippingZipCode: shippingAddress?.zipCode ?? null,
        shippingCountry: shippingAddress?.country ?? null,
        billingAddressLine1: billingAddress?.addressLine1 ?? null,
        billingAddressLine2: billingAddress?.addressLine2 ?? null,
        billingCity: billingAddress?.city ?? null,
        billingState: billingAddress?.state ?? null,
        billingZipCode: billingAddress?.zipCode ?? null,
        billingCountry: billingAddress?.country ?? null,
        cardBrand: selectedBuyer?.cardBrand ?? null,
        cardSummary: selectedBuyer?.cardSummary ?? null,
        expiryMonth: selectedBuyer?.expiryMonth ?? null,
        expiryYear: selectedBuyer?.expiryYear ?? null,
        retailCreditBalance: selectedBuyer?.retailCreditBalance ?? 0,
        creditCurrencyCode: selectedBuyer?.creditCurrencyCode ?? null,
        saleIds: selectedBuyer?.saleIds ?? null,
        salesGroupIds: selectedBuyer?.salesGroupIds ?? null,
        externalId: selectedBuyer?.externalId ?? null,
      });

      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getValues, selectedBuyer, setIsLoading, setSelectedBuyer, upsertBuyer]);

  return {
    disabled: disableEditing,
    adyenSession,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onCompleteExternalPayment,
    onPayAndComplete,
    onAdyenPaymentCompleted,
    onAdyenError,
  };
};
